/**
 * La invitación que trae la persona al alta o al acceso (`?invitation=<token>`).
 *
 * El token lo puso el guard de la app al mandarla aquí sin sesión; con él se
 * pregunta al backend quién invita a quién y a dónde (sin sesión: solo lo que
 * una PENDIENTE enseña) para que la pantalla lo diga, prerrellene el correo y,
 * al entrar, aterrice en la aceptación en vez de en el onboarding de quien
 * funda. El token viaja en el cuerpo de la petición, nunca en la URL del API.
 */
import { api } from '../services/api';
import { paramDeLaUrl, rutaConParametrosRetenidos } from '../utils/urlParams';

export interface InvitationPreview {
  org_name: string;
  inviter_name: string | null;
  email: string;
  name: string | null;
}

export const INVITATION_PARAM = 'invitation';

/** El token lo retiró de la URL `public/auth-scrub.js` antes de la analítica; se lee de su depósito. */
export function invitationTokenFromUrl(): string | null {
  return paramDeLaUrl(INVITATION_PARAM);
}

/** Devuelve null cuando no existe, ya se usó o el backend no contesta. */
export async function previewInvitation(token: string): Promise<InvitationPreview | null> {
  try {
    const { data } = await api.post<InvitationPreview>('/api/v1/orgs/invitations/preview', { token });
    return data;
  } catch {
    return null;
  }
}

/** Dónde aterriza en la app quien entra con una invitación en la mano. */
export function invitationLanding(token: string): string {
  return `/invitations/accept?token=${encodeURIComponent(token)}`;
}

/**
 * La misma invitación, en otra ruta de esta app con RECARGA (`window.location`):
 * el token va en la URL y `public/auth-scrub.js` lo retira al cargar.
 */
export function withInvitation(path: string, token: string | null, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams(extra);
  if (token) params.set(INVITATION_PARAM, token);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * La misma invitación, en otra ruta de esta app SIN recarga (`<router-link>`):
 * ni el token ni el correo van en la ruta, que la analítica sí ve (R2_INVITE-1,
 * 2026-09-19); quedan en el depósito de donde los lee `invitationTokenFromUrl`.
 */
export function withInvitationInApp(path: string, token: string | null, extra: Record<string, string> = {}): string {
  return rutaConParametrosRetenidos(path, { ...extra, [INVITATION_PARAM]: token });
}
